/**
 * Motion building blocks used across the app.
 *
 * Every one of these respects prefers-reduced-motion through Framer Motion's
 * MotionConfig (installed once in App), so no component needs to branch on it.
 */
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import type { HTMLMotionProps } from "framer-motion";
import type { ReactNode } from "react";
import {
  pageVariants,
  revealPresets,
  staggerChild,
  staggerParent,
  type RevealPreset,
} from "./variants";

/* ------------------------------------------------------------------ Reveal */
interface RevealProps extends Omit<HTMLMotionProps<"div">, "variants" | "children"> {
  children: ReactNode;
  /** Direction the element travels from. */
  preset?: RevealPreset;
  delay?: number;
  /** How much must be visible before it animates in. */
  amount?: number;
}

/**
 * Fires once when scrolled into view. Uses whileInView rather than an
 * IntersectionObserver of our own so Motion can cancel cleanly on unmount.
 */
export function Reveal({
  children,
  preset = "up",
  delay = 0,
  amount = 0.15,
  ...rest
}: RevealProps) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount, margin: "0px 0px -8% 0px" }}
      variants={revealPresets[preset]}
      transition={delay ? { delay } : undefined}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* ----------------------------------------------------------------- Stagger */
interface StaggerProps extends Omit<HTMLMotionProps<"div">, "variants" | "children"> {
  children: ReactNode;
  stagger?: number;
  delayChildren?: number;
  amount?: number;
}

/** Parent that cascades its <StaggerItem> children into view. */
export function Stagger({
  children,
  stagger = 0.06,
  delayChildren = 0,
  amount = 0.1,
  ...rest
}: StaggerProps) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount }}
      variants={staggerParent(stagger, delayChildren)}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, ...rest }: HTMLMotionProps<"div"> & { children: ReactNode }) {
  return (
    <motion.div variants={staggerChild} {...rest}>
      {children}
    </motion.div>
  );
}

/* --------------------------------------------------------- PageTransition */
export function PageTransition({ children }: { children: ReactNode }) {
  return (
    <motion.main
      id="main"
      className="page-main"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      {children}
    </motion.main>
  );
}

/* ------------------------------------------------------------- CountUp */
interface CountUpProps {
  to: number;
  suffix?: string;
  duration?: number;
  decimals?: number;
}

/**
 * Animated statistic driven off requestAnimationFrame so it stays in step with
 * the compositor. Renders the final value immediately under reduced motion.
 */
export function CountUp({ to, suffix = "", duration = 1.4, decimals = 0 }: CountUpProps) {
  const reduce = useReducedMotion();
  const [animated, setAnimated] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);

  // Derived during render — no effect needed for the reduced-motion case.
  const value = reduce ? to : animated;

  useEffect(() => {
    if (reduce) return;

    const node = ref.current;
    if (!node) return;

    let frame = 0;
    let started = false;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || started) return;
        started = true;

        const begin = performance.now();
        const tick = (now: number) => {
          const progress = Math.min((now - begin) / (duration * 1000), 1);
          setAnimated(to * (1 - Math.pow(1 - progress, 3)));
          if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [to, duration, reduce]);

  return (
    <span ref={ref}>
      {value.toFixed(decimals)}
      {suffix}
    </span>
  );
}

export * from "./variants";
